/**
 * Academy domain — custom-endpoint response records (PascalCase).
 * Source of truth: `docs/academy-api.types.ts` §6.3.
 */
import type {
  CertificateTargetType,
  DateTimeUtc,
  Guid,
  LearningProgressStatus,
  LiveEventStatusType,
  Long,
  ProductType,
  ReactionType,
  WatchType,
} from './academy-enums.model';
import { fromWireEnums } from './academy-enums.model';

export interface PlaybackTokenResponse {
  Token: string;
}

/**
 * `GET /LiveEvents/{id}/ingest` — ingest credentials for the producer.
 * `WhipUrl` — browser WHIP (low-latency, no recording).
 * `RtmpsUrl` / `RtmpsStreamKey` — OBS/encoder RTMPS (records to acervo).
 */
export interface LiveIngestResponse {
  WhipUrl: string;
  RtmpsUrl: string;
  RtmpsStreamKey: string;
}

export interface BrandingResponse {
  TenantId: Guid | null;
  DisplayName: string | null;
  LogoStorageKey: string | null;
  IsDefault: boolean;
}

/**
 * `GET /LiveEvents/{id}/subscribers` item — producer-view enrolled person.
 * Server-side search/paging; wire envelope is PascalCase `{ Items, Total }`, folded by
 * `toMineResult`. `PersonName` is `''` until the Person replica materializes (backend §8.1
 * T020); `TenantName` comes from the server's accounts cache (backend 014) and is `null` while
 * the account is not in it. `SubscribedOn` mirrors `LiveSubscription.AddedOnUtc` (nullable).
 */
export interface SubscriberItem {
  PersonId: Guid;
  PersonName: string;
  SubscribedOn: DateTimeUtc | null;
  TenantId: Guid;
  TenantName: string | null;
}

/**
 * `GET /LiveEvents/{id}/attendances` item — producer-view live attendance (backend 013,
 * `AttendanceItemViewModel`). Cross-tenant, live sessions only, open sessions first. Wire
 * envelope is PascalCase `{ Items, Total }`, folded by `toMineResult` (which also maps the
 * numeric `WatchType` to its name). One row per session: the same person in two tabs, or
 * after a rejoin, yields two rows. `PersonName` is `''` only when the person is unknown to
 * the person service. `TenantName` (backend 014) is `null` while the account is not in the
 * server's cache. `LeftOnUtc` is `null` while the session is open.
 */
export interface AttendanceItem {
  LiveAttendanceId: Guid;
  PersonId: Guid;
  PersonName: string;
  TenantId: Guid;
  TenantName: string | null;
  WatchType: WatchType;
  JoinedOnUtc: DateTimeUtc;
  LeftOnUtc: DateTimeUtc | null;
  WatchedSeconds: number;
}

/**
 * `GET /LiveEvents/{id}/chat` item — producer-view chat history (backend 013c/d,
 * `ChatHistoryItemViewModel`). All tenants, newest first. Wire envelope is PascalCase
 * `{ Items, Total }`, folded by `toMineResult`. A message removed by moderation comes back
 * with `IsRemoved = true` and an empty `Text`. `PersonName` is `''` only when the person is
 * unknown to the person service. `TenantName` and `IsProducer` since backend 014: the account
 * name is `null` while the account is not in the server's cache, and `IsProducer` was decided
 * when the message was sent.
 */
export interface ChatHistoryItem {
  ChatMessageId: Guid;
  PersonId: Guid;
  PersonName: string;
  TenantId: Guid;
  TenantName: string | null;
  Text: string;
  SentOnUtc: DateTimeUtc;
  IsRemoved: boolean;
  IsProducer: boolean;
}

/**
 * `GET /LiveEvents/{id}/chat/history` item — the chat as a viewer reads it (backend 014,
 * `ChatViewerItemViewModel`). Plain PascalCase array, newest first. On a single-room live it
 * holds every account's messages; on a live from before 014, only the caller's account.
 * Removed messages are left out, and the writer's account is never sent: a client must not
 * learn who the other clients are. `PersonName` is `''` only when the person is unknown to
 * the person service.
 */
export interface ChatViewerItem {
  ChatMessageId: Guid;
  PersonId: Guid;
  PersonName: string;
  Text: string;
  SentOnUtc: DateTimeUtc;
  IsProducer: boolean;
}

/**
 * `GET /LiveEvents/{id}/chat/muted` item — who is muted in the live's chat, producer view
 * (backend round 24, `MutedParticipantViewModel`). Plain PascalCase array, every account,
 * empty when nobody is muted. `TenantName` is `null` while the account is not in the server's
 * cache.
 */
export interface MutedParticipantItem {
  PersonId: Guid;
  PersonName: string;
  TenantId: Guid;
  TenantName: string | null;
  MutedOnUtc: DateTimeUtc | null;
}

/**
 * `GET /Certificates/issued` item — producer-view issued certificate. Server-side `search`
 * (holder name) and `top`/`skip` paging; wire envelope is PascalCase `{ Items, Total }`,
 * folded by `toMineResult` (which also maps the numeric `CertificateTargetType` to its name).
 * Server order is fixed (`IssuedOnUtc` desc, then `PersonName` asc) — there is no sort
 * parameter. `PersonName` is `''` until the Person replica materializes. `TargetTitle` is
 * resolved server-side in the same batch (backend 012-D). Deliberately carries no
 * `VerificationCode`: that stays on the holder's own `CertificateResponse` (`/mine`).
 */
export interface IssuedCertificateItem {
  CertificateId: Guid;
  PersonId: Guid;
  PersonName: string;
  TenantId: Guid;
  CertificateTargetType: CertificateTargetType;
  TargetId: Guid;
  /**
   * Course/path title, never `null`. A deleted course/path still carries its title; `''` only
   * when the target never existed.
   */
  TargetTitle: string;
  CompletedOnUtc: DateTimeUtc;
  IssuedOnUtc: DateTimeUtc;
}

export interface UpvoteResponse {
  UpvoteCount: number;
}

export interface ResumeResponse {
  LiveEventId?: Guid;
  WatchedSeconds?: number;
  seconds?: number;
  Seconds?: number;
}

export interface CourseItemCreatedResponse {
  CourseItemId: Guid;
}

/**
 * `POST /Courses/{id}/items/upload` — Direct Creator Upload handshake
 * (`guide-upload-video.md`): the API creates the VOD `VideoAsset` + `CourseItem`
 * and hands back a one-time Cloudflare Stream URL (~30 min TTL, ≤200 MB).
 */
export interface CourseItemUploadResponse {
  CourseItemId: Guid;
  VideoAssetId: Guid;
  UploadUrl: string;
}

/**
 * `POST /Courses/{id}/cover` — multipart cover upload. The Academy API forwards the
 * binary to the StorageService and returns the stored URL. `CoverUrl` is null only
 * when the backend could not resolve a URL.
 */
export interface CoverResponse {
  CoverUrl: string | null;
}

/**
 * `POST /Courses/{id}/attachments` item — support-material descriptor returned on
 * upload and by `GET /Courses/{id}/attachments` (the binary lives in StorageService).
 */
export interface CourseAttachmentResponse {
  CourseAttachmentId: Guid;
  FileName: string;
  ContentType: string;
  Url: string | null;
}

/**
 * `POST /LiveEvents/{id}/attachments` item — support-material descriptor returned on
 * upload and by `GET /LiveEvents/{id}/attachments`. Mirrors `CourseAttachmentResponse`
 * exactly (backend feature 012): the binary lives in StorageService and `Url` is the
 * only way to reach it — the OData `LiveAttachments` set no longer exposes `Url`,
 * `StorageObjectId` or `StorageKey`. It carries no `LiveEventId`, so callers must key
 * state by the id they asked for, never by a field on the item.
 */
export interface LiveAttachmentResponse {
  LiveAttachmentId: Guid;
  FileName: string;
  ContentType: string;
  Url: string | null;
}

export interface LearningPathCourseCreatedResponse {
  LearningPathCourseId: Guid;
}

export interface CourseProgressResponse {
  CourseId: Guid;
  /** 0..100 */
  Percent: number;
  CompletedItemIds: Guid[];
}

export interface PathProgressResponse {
  LearningPathId: Guid;
  /** 0..100 */
  Percent: number;
  CompletedCourseIds: Guid[];
}

export interface TranscriptMatch {
  StartSeconds: number;
  EndSeconds: number;
  Text: string;
  LanguageCode: string;
}

export interface CertificateVerificationResponse {
  Valid: boolean;
  HolderName: string | null;
  TargetTitle: string | null;
  CompletedOnUtc: DateTimeUtc | null;
}

/** Reaction counts (`GET /LiveEvents/{id}/reactions`): map keyed by ReactionType name. */
export type ReactionCounts = Partial<Record<ReactionType, Long>>;

/**
 * Normalized paginated result for the `/mine` endpoints — INTERNAL shape (lowercase
 * `items`/`total`) consumed by the stores. The WIRE envelope is PascalCase
 * (`{ Items, Total }`); `toMineResult` translates the wire into this.
 */
export interface MineResult<T> {
  items: T[];
  total: number;
}

/**
 * Fold a `/mine` response into the internal `MineResult<T>`. The deployed API has
 * been observed answering THREE different ways for the SAME endpoint, so all are
 * handled (confirmed live 2026-06-17 on `GET /Courses/mine`):
 *   - PascalCase envelope `{ Items, Total }` — the §6.3 convention, what the app
 *     actually receives;
 *   - a BARE ARRAY `[ {…} ]` — no wrapper;
 *   - a lowercase `{ items, total }` envelope — defensive.
 * `res` is `unknown` on purpose: the wire shape is not stable enough to type, and
 * the casing must be probed at runtime. Enum fields (e.g. `ProductType`) are
 * translated from numeric wire codes to names via `fromWireEnums`; `total` falls
 * back to the item count when absent.
 */
export function toMineResult<T>(res: unknown): MineResult<T> {
  if (Array.isArray(res)) {
    const items = fromWireEnums(res as T[]);
    return { items, total: items.length };
  }
  const env = (res ?? {}) as { Items?: T[]; Total?: number; items?: T[]; total?: number };
  const items = fromWireEnums(env.Items ?? env.items ?? []);
  return { items, total: env.Total ?? env.total ?? items.length };
}

/**
 * `GET /Courses/mine` item — student-view course summary.
 * `Status` is the student's consumption progress, not the authoring status (`CourseStatusType`).
 * Ordered by most-recently-watched first.
 */
export interface MyCourseSummary {
  CourseId: Guid;
  Title: string;
  ProductType: ProductType;
  /** `null` when no cover is set. */
  CoverUrl: string | null;
  TotalItems: number;
  CompletedItems: number;
  /** Integer 0–100. */
  Percent: number;
  Status: LearningProgressStatus;
  /** `null` if the student has never watched — use to build the "Continue" CTA. */
  LastWatchedItemId: Guid | null;
  LastWatchedOnUtc: DateTimeUtc | null;
  /** `null` until the certificate is issued. */
  CertificateId: Guid | null;
}

/**
 * `GET /LearningPaths/mine` item — student-view learning-path summary.
 * `Percent` is completed-courses ÷ total (not weighted average).
 */
export interface MyLearningPathSummary {
  LearningPathId: Guid;
  Title: string;
  ProductType: ProductType;
  CoverUrl: string | null;
  TotalCourses: number;
  CompletedCourses: number;
  /** Integer 0–100. */
  Percent: number;
  Status: LearningProgressStatus;
  /** Next non-completed course in path order; `null` when the path is completed. */
  NextCourseId: Guid | null;
  CertificateId: Guid | null;
}

/**
 * `GET /LiveSubscriptions/mine` item — a live event the student saved (subscribed to).
 * ⚠ `CoverUrl` is always `null` (live events have no cover today — future feature).
 * ⚠ `ReminderEnabled` is always `true` for now (subscription IS the opt-in).
 * `VideoAssetId` is non-null when there is a recording available (`Status === 'Recorded'`).
 */
export interface MyLiveSubscriptionSummary {
  LiveEventId: Guid;
  Title: string;
  ProductType: ProductType;
  CoverUrl: string | null;
  ScheduledStartOnUtc: DateTimeUtc;
  Status: LiveEventStatusType;
  ReminderEnabled: boolean;
  VideoAssetId: Guid | null;
}
